"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonLoading, IonToast } from "@ionic/react"
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Stack,
  Container,
  Grid,
} from "@mui/material"
import { Visibility, CheckCircle, Warning, Inventory, Refresh } from "@mui/icons-material"
import { styled } from "@mui/material/styles"
import { approveBarRequest, approveBarRequest_branch, getPendingBarRequestsWithItems, getPendingBarRequestsWithItems_sales } from "../../../api/stock-transfer/stock-transfer"

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
  border: "1px solid rgba(0,0,0,0.06)",
}))

const HeaderCard = styled(Box)(({ theme }) => ({
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  padding: theme.spacing(3),
  borderRadius: "12px 12px 0 0",
}))

interface RequestItem {
  id: string
  product_id: string
  product_name: string
  name: string
  quantity: number
  available: number
  transferred?: number
}

interface TransferRequest {
  id: string
  request_id: string
  requested_by: string
  requested_by_id: string
  items: RequestItem[]
  status: "pending" | "completed" | "partial"
  date: string
  created_at: string
  notes?: string
}

const WarehouseTransferPage: React.FC = () => {
  const [requests, setRequests] = useState<TransferRequest[]>([])
  const [loading, setLoading] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState("")
  const [transferDialogOpen, setTransferDialogOpen] = useState(false)
  const [currentRequest, setCurrentRequest] = useState<TransferRequest | null>(null)
  const [transferQuantities, setTransferQuantities] = useState<Record<string, number>>({})

  useEffect(() => {
    fetchPendingRequests()
  }, [])

  const fetchPendingRequests = async () => {
    setLoading(true)
    try {
      const response = await getPendingBarRequestsWithItems_sales()
      console.log({ response })
      setRequests(response.data.data)
    } catch (error) {
      console.error(error)
      setToastMessage("Failed to fetch requests")
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  const openTransferDialog = (request: TransferRequest) => {
    const quantities: Record<string, number> = {}
    request.items.forEach((item) => {
      quantities[item.product_id] = Math.min(item.available, item.quantity)
    })
    setTransferQuantities(quantities)
    setCurrentRequest(request)
    setTransferDialogOpen(true)
  }

  const handleQuantityChange = (itemId: string, value: number) => {
    setTransferQuantities((prev) => ({
      ...prev,
      [itemId]: Math.max(0, value),
    }))
  }

  const handleTransfer = async () => {
    if (!currentRequest) return

    setLoading(true)
    setTransferDialogOpen(false)

    const payload = {
      bar_request_id: currentRequest?.request_id,
      requested_by: currentRequest?.requested_by_id,
      items: currentRequest.items.map((item) => ({
        product_id: item.product_id,
        quantity: transferQuantities[item.product_id],
      })),
    }

    try {
      const response = await approveBarRequest_branch(payload)
      console.log(response)
      fetchPendingRequests()
      setToastMessage("Transfer processed successfully!")
    } catch (error) {
      setToastMessage("Error processing transfer")
    } finally {
      setLoading(false)
      setShowToast(true)
      setCurrentRequest(null)
      setTransferQuantities({})
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "success"
      case "partial":
        return "warning"
      default:
        return "default"
    }
  }

  const getItemStatusColor = (item: RequestItem) => {
    if (item.available === 0) return "error"
    if (item.available >= item.quantity) return "success"
    return "warning"
  }

  const getItemStatusText = (item: RequestItem) => {
    if (item.available === 0) return "Out of stock"
    if (item.available >= item.quantity) return "Available"
    return "Low stock"
  }

  return (
    <IonPage className="pushDown">
      <IonHeader>
        <IonToolbar>
          <IonTitle>Warehouse Transfers</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <Container maxWidth="lg" sx={{ py: 2 }}>
          {/* Header */}
          <StyledCard sx={{ mb: 3 }}>
            <HeaderCard>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box>
                  <Typography variant="h6" fontWeight="600" color="#1e293b">
                    Transfer Requests
                  </Typography>
                  <Typography variant="body2" color="#64748b" sx={{ mt: 0.5 }}>
                    Manage pending warehouse transfer requests
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={fetchPendingRequests}
                  disabled={loading}
                  sx={{ color: "#64748b", borderColor: "#d1d5db" }}
                >
                  Refresh
                </Button>
              </Box>
            </HeaderCard>
          </StyledCard>

          {/* Requests List */}
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : requests?.length === 0 ? (
            <StyledCard>
              <CardContent sx={{ textAlign: "center", py: 6 }}>
                <Inventory sx={{ fontSize: 64, color: "#cbd5e1", mb: 2 }} />
                <Typography variant="h6" color="#64748b" sx={{ mb: 1 }}>
                  No Transfer Requests
                </Typography>
                <Typography variant="body2" color="#94a3b8">
                  All transfer requests have been processed
                </Typography>
              </CardContent>
            </StyledCard>
          ) : (
            <Grid container spacing={2}>
              {requests?.map((request, i) => (
                <Grid item xs={12} key={i}>
                  <StyledCard>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                        <Box>
                          <Typography variant="h6" fontWeight="600" color="#1e293b">
                            Request #{i + 1}
                          </Typography>
                          <Typography variant="body2" color="#64748b">
                            By {request.requested_by} • {formatDate(request.created_at)}
                          </Typography>
                        </Box>
                        <Chip
                          label={request.status}
                          color={getStatusColor(request.status) as any}
                          sx={{ textTransform: "capitalize" }}
                        />
                      </Box>

                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <Chip
                            label={`${request.items.length} items`}
                            size="small"
                            sx={{ bgcolor: "#f1f5f9", color: "#475569" }}
                          />
                          {request.items.some((item) => item.available < item.quantity) && (
                            <Chip
                              label="Low stock"
                              size="small"
                              color="warning"
                              icon={<Warning sx={{ fontSize: 16 }} />}
                            />
                          )}
                        </Box>

                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Visibility />}
                            onClick={() => openTransferDialog(request)}
                            sx={{ color: "#64748b", borderColor: "#d1d5db" }}
                          >
                            View
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<CheckCircle />}
                            onClick={() => openTransferDialog(request)}
                            disabled={
                              request.status !== "pending" || request.items.every((item) => item.available === 0)
                            }
                            sx={{ bgcolor: "#1e293b", "&:hover": { bgcolor: "#334155" } }}
                          >
                            Process
                          </Button>
                        </Stack>
                      </Box>
                    </CardContent>
                  </StyledCard>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Transfer Dialog */}
          <Dialog open={transferDialogOpen} onClose={() => setTransferDialogOpen(false)} fullWidth maxWidth="md">
            <DialogTitle sx={{ pb: 1 }}>
              <Typography variant="h6" fontWeight="600">
                Transfer Request Details
              </Typography>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0 }}>
              {currentRequest && (
                <Box sx={{ p: 3 }}>
                  {/* Request Info */}
                  <Box sx={{ mb: 3, p: 2, bgcolor: "#f8fafc", borderRadius: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="#64748b">
                          Requested by
                        </Typography>
                        <Typography variant="body1" fontWeight="500">
                          {currentRequest.requested_by}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="#64748b">
                          Date
                        </Typography>
                        <Typography variant="body1" fontWeight="500">
                          {formatDate(currentRequest.created_at)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Warning Alert */}
                  {currentRequest.items.some((item) => item.available < item.quantity) && (
                    <Alert severity="warning" sx={{ mb: 3 }}>
                      Some items have insufficient stock available
                    </Alert>
                  )}

                  {/* Items Table */}
                  <TableContainer component={Paper} sx={{ borderRadius: 2, border: "1px solid #e2e8f0" }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: "#f8fafc" }}>
                          <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Product</TableCell>
                          <TableCell align="right" sx={{ fontWeight: "600", color: "#374151" }}>
                            Requested
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: "600", color: "#374151" }}>
                            Available
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: "600", color: "#374151" }}>
                            Status
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: "600", color: "#374151" }}>
                            Transfer
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {currentRequest.items.map((item) => (
                          <TableRow key={item.product_id} hover>
                            <TableCell sx={{ color: "#374151" }}>{item.name}</TableCell>
                            <TableCell align="right">
                              <Chip label={item.quantity} size="small" sx={{ bgcolor: "#f1f5f9", color: "#475569" }} />
                            </TableCell>
                            <TableCell align="right">
                              <Chip label={item.available} size="small" sx={{ bgcolor: "#f1f5f9", color: "#475569" }} />
                            </TableCell>
                            <TableCell align="right">
                              <Chip
                                label={getItemStatusText(item)}
                                size="small"
                                color={getItemStatusColor(item) as any}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                type="number"
                                size="small"
                                value={transferQuantities[item.product_id] || 0}
                                onChange={(e) =>
                                  handleQuantityChange(item.product_id, Number.parseInt(e.target.value) || 0)
                                }
                                inputProps={{
                                  min: 0,
                                  max: item.available,
                                }}
                                sx={{ width: 80 }}
                                disabled={item.available === 0}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
              <Button onClick={() => setTransferDialogOpen(false)} sx={{ color: "#64748b" }}>
                Cancel
              </Button>
              <Button
                onClick={handleTransfer}
                variant="contained"
                disabled={
                  !currentRequest ||
                  currentRequest.items.every((item) => (transferQuantities[item.product_id] || 0) === 0)
                }
                startIcon={<CheckCircle />}
                sx={{ bgcolor: "#1e293b", "&:hover": { bgcolor: "#334155" } }}
              >
                Confirm Transfer
              </Button>
            </DialogActions>
          </Dialog>

          <IonLoading isOpen={loading} message="Processing..." />
          <IonToast
            isOpen={showToast}
            onDidDismiss={() => setShowToast(false)}
            message={toastMessage}
            duration={3000}
            position="top"
          />
        </Container>
      </IonContent>
    </IonPage>
  )
}

export default WarehouseTransferPage
